import { useQuery } from "@tanstack/react-query";

/**
 * Hook to fetch applications data with React Query
 * Returns { applications: [], totalCount: N } for consistency with shared/hooks/useApplications
 */
export const useApplicationsData = () => {
  const token = localStorage.getItem("token");
  
  const queryResult = useQuery({
    queryKey: ["applications"],
    queryFn: async () => {
      const token = localStorage.getItem("token");
      
      if (!token) {
        return { applications: [], totalCount: 0 };
      }
      
      const res = await fetch("/api/applications/get", {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!res.ok) {
        throw new Error("Failed to fetch applications");
      }
      
      const applicationsArray = await res.json();
      
      // Return in consistent format matching shared/hooks/useApplications
      return {
        applications: applicationsArray,
        totalCount: applicationsArray.length,
      };
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!token, // Only run if token exists
    retry: 1, // Retry once on failure
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
  
  return queryResult;
};


/**
 * Calculate application statistics from applications array
 */
export const calculateApplicationStats = (applications) => {
  if (!applications || !Array.isArray(applications)) {
    return {
      jdsAnalyzed: 0,
      resumesDownloaded: 0,
      avgMatchScore: 0,
      totalApplications: 0,
    };
  }

  // JD Analysis: Check if jobDescription is not empty
  const jdsAnalyzed = applications.filter(
    (app) => app.jobDescription && app.jobDescription.trim().length > 0
  ).length;

  // Resume Downloads: Check if resumeUrl is not null
  const resumesDownloaded = applications.filter(
    (app) => app.resumeUrl !== null
  ).length;

  // Total Applications: Simple count
  const totalApplications = applications.length;

  // Average Match Score: Hybrid approach
  const avgMatchScore = calculateAverageMatchScore(applications);

  return {
    jdsAnalyzed,
    resumesDownloaded,
    avgMatchScore,
    totalApplications,
  };
};

/**
 * Calculate average match score using hybrid approach
 * Uses API matchScore if available, otherwise calculates client-side
 */
export const calculateAverageMatchScore = (applications) => {
  // Filter applications that have been analyzed
  // For now, we'll use jobDescription presence as indicator
  // Later this will be replaced with app.isAnalyzed flag
  const analyzedApps = applications.filter(
    (app) => app.jobDescription && app.jobDescription.trim().length > 0
  );

  if (analyzedApps.length === 0) return 0;

  const totalScore = analyzedApps.reduce((sum, app) => {
    // If API provides matchScore, use it (future enhancement)
    if (app.matchScore !== undefined && app.matchScore !== null) {
      return sum + app.matchScore;
    }

    // Otherwise, calculate client-side (current fallback)
    return sum + calculateClientSideMatchScore(app);
  }, 0);

  return Math.round(totalScore / analyzedApps.length);
};

/**
 * Client-side match score calculation (fallback when API doesn't provide matchScore)
 * This will be replaced when API provides proper matchScore
 */
export const calculateClientSideMatchScore = (application) => {
  // Base score for applications with job descriptions
  let score = 70;

  // Bonus for having extracted tools (indicates deeper analysis)
  if (application.extractedTools?.length > 10) {
    score += 10;
  }

  // Bonus for having relevant achievements selected
  if (application.relevantAchievements?.some((ra) => ra.selected > 0)) {
    score += 10;
  }

  // Bonus for interview status (indicates good match)
  if (application.status === "interview") {
    score += 10;
  }

  // Bonus for having company research (indicates thorough preparation)
  if (application.companyResearch && Object.keys(application.companyResearch).length > 0) {
    score += 5;
  }

  // Cap at 95% to maintain realistic expectations
  return Math.min(score, 95);
};

/**
 * Helper function to determine if user has meaningful application data
 */
export const hasApplicationData = (applications) => {
  return applications && applications.length > 0;
};

/**
 * Get recent applications (last 7 days) - for future use
 */
export const getRecentApplications = (applications, days = 7) => {
  if (!applications || !Array.isArray(applications)) return [];

  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - days);

  return applications.filter((app) => {
    const appDate = new Date(app.createdAt || app.applicationDate);
    return appDate >= cutoffDate;
  });
};

/**
 * Get applications by status - for future use
 */
export const getApplicationsByStatus = (applications, status) => {
  if (!applications || !Array.isArray(applications)) return [];
  return applications.filter((app) => app.status === status);
};